import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiForgotPassword } from "../lib/api";
import { Sprout, Loader2, Mail, Lock, User, Sparkles, CheckCircle2 } from "lucide-react";

const formatErr = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || String(d);
};

export default function AuthPage({ initialMode = "register" }) {
  const { login, register, loginWithGoogle, bootError } = useAuth();
  const [mode, setMode] = useState(initialMode); // register | login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState(bootError || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg(""); setBusy(true);
    try {
      if (mode === "forgot") {
        await apiForgotPassword(email, window.location.origin);
        setMsg("If an account exists for that email, we've sent a reset link. Check your inbox.");
      } else if (mode === "register") {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const switchMode = (m) => { setMode(m); setErr(""); setMsg(""); };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[42%] bg-forest-900 text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-6 w-6" strokeWidth={2.4} /></div>
          <span className="font-heading font-extrabold text-2xl">Assort<span className="text-ai">IQ</span></span>
        </div>
        <div>
          <h1 className="font-heading text-4xl font-extrabold leading-tight">AI-driven category<br />assortment optimization</h1>
          <p className="mt-4 text-white/60 max-w-md">Grow/keep/reduce/remove decisions, shelf-space plans, what-if scenarios and store analytics — powered by Claude Sonnet 4.6.</p>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-ai/15 border border-ai/30 px-4 py-2 text-sm text-ai">
            <Sparkles className="h-4 w-4" /> Start your 7-day free trial
          </div>
        </div>
        <div className="text-white/40 text-xs font-mono">From $997 / year after trial · cancel anytime</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <Sprout className="h-6 w-6 text-ai" /><span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-forest-800">{mode === "register" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back"}</h2>
          <p className="text-sm text-forest-500 mt-1">{mode === "register" ? "7-day free trial — no charge today." : mode === "forgot" ? "Enter your email and we'll send you a reset link." : "Sign in to your AssortIQ workspace."}</p>

          {mode !== "forgot" && (
            <>
              <button data-testid="google-signin-button" onClick={loginWithGoogle}
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-md border border-border bg-white py-2.5 text-sm font-semibold text-forest-800 transition-colors hover:bg-forest-50">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" /> Continue with Google
              </button>
              <div className="my-5 flex items-center gap-3 text-xs text-forest-400">
                <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          <form onSubmit={submit} className={`space-y-3 ${mode === "forgot" ? "mt-6" : ""}`}>
            {mode === "register" && (
              <div className="relative">
                <User className="h-4 w-4 text-forest-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
                  className="w-full rounded-md border border-border pl-9 pr-3 py-2.5 text-sm text-forest-900 placeholder-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-800" />
              </div>
            )}
            <div className="relative">
              <Mail className="h-4 w-4 text-forest-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                className="w-full rounded-md border border-border pl-9 pr-3 py-2.5 text-sm text-forest-900 placeholder-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-800" />
            </div>
            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="h-4 w-4 text-forest-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="auth-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
                  className="w-full rounded-md border border-border pl-9 pr-3 py-2.5 text-sm text-forest-900 placeholder-forest-400 focus:outline-none focus:ring-2 focus:ring-forest-800" />
              </div>
            )}
            {mode === "login" && (
              <div className="text-right">
                <button type="button" data-testid="forgot-password-link" onClick={() => switchMode("forgot")}
                  className="text-xs font-semibold text-ai hover:underline">Forgot password?</button>
              </div>
            )}
            {err && <p data-testid="auth-error" className="text-sm text-[#C0261F]">{err}</p>}
            {msg && <p data-testid="auth-message" className="flex items-start gap-2 text-sm text-action-keep"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />{msg}</p>}
            <button data-testid="auth-submit-button" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 rounded-md bg-forest-900 text-white py-2.5 text-sm font-bold uppercase tracking-wider transition-colors hover:bg-forest-800 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "register" ? "Start free trial" : mode === "forgot" ? "Send reset link" : "Sign in"}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-forest-500">
            {mode === "forgot" ? (
              <button data-testid="auth-toggle" onClick={() => switchMode("login")} className="font-semibold text-ai hover:underline">Back to sign in</button>
            ) : (
              <>
                {mode === "register" ? "Already have an account?" : "New to AssortIQ?"}{" "}
                <button data-testid="auth-toggle" onClick={() => switchMode(mode === "register" ? "login" : "register")}
                  className="font-semibold text-ai hover:underline">
                  {mode === "register" ? "Sign in" : "Create one"}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
